'use client'

import type {
  ProposalAccount,
  ProposalOrder,
} from '@moment/horizon-data-access/proposal'
import type { ProposalDetailsQueryParams } from '@/app/(dashboard)/proposals/[id]/page'
import type { RootState } from '@/app/store'
import type { ProposalModelCompliance } from '@/legacy-api/proposals-slice-types'
import type { AccountDetail } from '@/types/api/accounts'
import type { PaneType } from '@/types/enums/pane-type'

import { Badge, BadgeVariant } from '@moment/design-system-ui/badge'
import { isEqual } from 'lodash'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react' // import Table from '@/components/atomic/table'

import Icon, { IconName } from '@/components/atomic/icon'
import Search from '@/components/atomic/search'
import { Select } from '@/components/atomic/select'
import {
  BodyDefault,
  BodySmall,
  BodySubtext,
  BodyXS,
  FontWeight,
} from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { aggregatePosition } from '@/components/molecules/account-holdings-table/helpers'
import { SummarySection } from '@/components/molecules/account-summary-section'
import { ComplianceList } from '@/components/molecules/compliance-list'
import ConfirmOrderRemoveDialog from '@/components/molecules/confirm-proposal-order-remove-dialog'
import ProposalAccountHoldingsTable from '@/components/molecules/proposal-account-holdings-table'
import ProposalBondSwapDialog from '@/components/molecules/proposal-bond-swap-dialog'
import ProposalEditOrderDialog from '@/components/molecules/proposal-edit-order-dialog'
import { TargetNumberLine } from '@/components/molecules/target-number-line'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { useCancelProposalOrder } from '@/hooks/use-cancel-proposal-order'
import useUrlQueryParams from '@/hooks/use-url-query-params'
import { useGetProposalQuery } from '@/legacy-api/proposals-slice'
import { extractOrderMarketValue } from '@/portfolio/proposal-builder/data/helpers/extract-order-market-value'
import { ConstraintInstanceType } from '@/portfolio/shared/constraint/types'
import { ManagementType } from '@/types/api/accounts'
import {
  type ComplianceCheck,
  ComplianceCheckType,
  type MarketValueCompliance,
  type WeightCompliance,
} from '@/types/api/compliance'
import { formatMoney, formatPercentage } from '@/utils/presentation/formatting'

import {
  AccountHoldingsContainer,
  AccountMetadataAndSleeveSelectorContainer,
  AccountMetadataContainer,
  AccountNameAndManagementTypeContainer,
  BeforeAndAfterContainer,
  ColorSwatch,
  ContentContainer,
  HoldingsBorderContainer,
  IconContainer,
  NavigationContainer,
  PortfolioAllocationContainer,
  PortfolioHeaderContainer,
  PortfolioOverviewContainer,
  PromptSectionContainer,
  ProposalAccountDetailsContainer,
  ProposalAccountDetailsContentContainer,
  ProposalAccountDetailsContentLayout,
  ProposalAccountDetailsHeaderContainer,
  ProposalAccountDetailsMainContent,
  SearchContainer,
  SectionContainer,
  SelectPortfolioOverlayContainer,
  SwatchAndTitleContainer,
  VerticalSeparator,
} from './styles'

type ProposalAccountDetailsProps = {
  onTogglePane: (paneType: PaneType | null, orderIdOrIsin?: string) => void
  onBackToTable: () => void
  proposalId: string
}

export enum ProposalAccountDetailsTabs {
  Summary = 'Summary',
  Compliance = 'Compliance',
  Actions = 'Actions',
}

export interface ProposalTrade {
  id: string
  name: string
  percentage: string
  accountName: string
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ProposalAccountDetails({
  proposalId,
  onTogglePane,
  onBackToTable,
}: ProposalAccountDetailsProps) {
  const proposalAccountCheckpoint = useRef<ProposalAccount | null>(null)
  const [isSwapDialogOpen, setIsSwapDialogOpen] = useState(false)
  const [selectedOrderForSwap, setSelectedOrderForSwap] =
    useState<ProposalOrder | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedOrderForEdit, setSelectedOrderForEdit] =
    useState<ProposalOrder | null>(null)
  const [rowNodeLoading, setRowNodeLoading] = useState<Record<string, boolean>>(
    {},
  )
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isSidePaneVisible = false

  const { data: proposal } = useGetProposalQuery(
    { proposal_id: proposalId },
    { skip: !proposalId, pollingInterval: 5000 },
  )

  const selectedAccount = useTieredSelector(
    (state: RootState) => state.proposals.selectedAccount,
    (params: ProposalDetailsQueryParams) => params.selectedAccount,
  )

  const accountDetails = useMemo(() => {
    if (
      !selectedAccount ||
      isEqual(
        proposalAccountCheckpoint.current,
        proposal?.accounts[selectedAccount],
      )
    )
      return null
    return proposal?.accounts[selectedAccount] ?? null
  }, [selectedAccount, proposal])

  // The generated ProposalAccount carries the proposal-spec AccountDetail; this
  // proposal-review surface still reads the hand-written accounts AccountDetail.
  const before = accountDetails?.before as unknown as AccountDetail | undefined
  const after = (accountDetails?.after ?? before) as unknown as
    | AccountDetail
    | undefined

  // Backend doesn't return negative cash in after.statistics.cash_market_value,
  // so we compute it client-side: initialCash + sellOrders - buyOrders
  const proposedCash = useMemo(() => {
    if (!proposal || !selectedAccount)
      return after?.statistics.cash_market_value ?? 0
    const initialCash = before?.statistics.cash_market_value ?? 0
    const orders = extractOrderMarketValue(proposal, selectedAccount)
    return initialCash + orders.sellOrders - orders.buyOrders
  }, [
    proposal,
    selectedAccount,
    before?.statistics.cash_market_value,
    after?.statistics.cash_market_value,
  ])
  const isNegativeCash = proposedCash < 0

  const { setQueryParams } = useUrlQueryParams<ProposalDetailsQueryParams>()

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
      }
    }
  }, [])

  const searchTerm = useTieredSelector(
    (state: RootState) => state.proposals.searchTerm,
    (params: ProposalDetailsQueryParams) => params.searchTerm,
  )

  // Handle proposal order actions (remove/swap) for holdings table
  const {
    handleRemoveOrder: handleOrderRemoveFromHoldings,
    handleRemoveClick: handleOrderRemoveClick,
    isRemoveDialogOpen: isOrderRemoveDialogOpen,
    setIsRemoveDialogOpen: setIsOrderRemoveDialogOpen,
    selectedTradeToRemove: selectedOrderToRemove,
  } = useCancelProposalOrder({
    proposal,
    onOrderRemoved: () => {
      // Close the side pane when an order is removed
      onTogglePane(null)
    },
  })

  const handleOrderRemoveAction = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
    (order: any) => {
      handleOrderRemoveClick(order.moment_order_id)
    },
    [handleOrderRemoveClick],
  )

  const handleOrderSwapAction = useCallback((order: ProposalOrder) => {
    // Clear any existing loading states first
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current)
      loadingTimerRef.current = null
    }

    // Reset any existing loading states
    setRowNodeLoading({})

    // Set the order for the swap dialog
    setSelectedOrderForSwap(order)
    setIsSwapDialogOpen(true)
  }, [])

  const handleOrderEditAction = useCallback((order: ProposalOrder) => {
    // Set the order for the edit dialog
    setSelectedOrderForEdit(order)
    setIsEditDialogOpen(true)
  }, [])

  const handleSwapConfirm = useCallback(
    (_selectedBond: ProposalOrder) => {
      // Clear any existing timer
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
      }

      // Close the dialog first
      setIsSwapDialogOpen(false)

      // Set loading state for this row (using ISIN as the key since that's what the holdings table uses)
      const instrumentId = selectedOrderForSwap?.instrument_id || ''
      setRowNodeLoading((prev) => ({
        ...prev,
        [instrumentId]: true,
      }))

      // Always ensure we clear loading state after 3 seconds
      loadingTimerRef.current = setTimeout(() => {
        setRowNodeLoading((prev) => {
          const updated = { ...prev }
          delete updated[instrumentId]
          return updated
        })
        loadingTimerRef.current = null
      }, 3000)
    },
    [selectedOrderForSwap],
  )

  //
  // Select values from Redux / query params
  //

  const activeTab =
    useTieredSelector(
      (state: RootState) => state.proposals.accountDetailsTab,
      (params: ProposalDetailsQueryParams) => params.accountDetailsTab,
    ) || ProposalAccountDetailsTabs.Summary

  // We will use the selected sleeve to show sleeve-level summary and
  // compliance data. If there is no sleeve selected (i.e., the full
  // account is to be shown), then we will use account-level summary
  // and compliance data. This should stay in sync with the accounts
  // table in the proposal details page so the data is consistent.
  const selectedSleeve = useTieredSelector(
    (state: RootState) => state.proposals.selectedSleeve,
    (params: ProposalDetailsQueryParams) => params.selectedSleeve,
  )

  const managementType = useMemo(() => {
    if (!after) return null
    return after.management_type
  }, [after])

  //
  // Pagination helpers
  //

  const pageNumber = useMemo(() => {
    if (!proposal) return null
    // biome-ignore lint/complexity/useIndexOf: Bulk suppression as part of biome migration
    return Object.keys(proposal.accounts).findIndex(
      (key) => key === selectedAccount,
    )
  }, [proposal, selectedAccount])

  const totalPages = useMemo(() => {
    if (!proposal) return null
    return Object.keys(proposal.accounts).length
  }, [proposal])

  const getAccountByIndex = useCallback(
    (index: number) => {
      if (!proposal) return undefined
      return Object.keys(proposal.accounts)[index]
    },
    [proposal],
  )

  const handleNextAccount = useCallback(() => {
    if (!proposal || pageNumber === null || totalPages === null) return
    const nextIndex = (pageNumber + 1) % totalPages

    // Close the side pane when switching sleeves
    onTogglePane(null)

    setQueryParams(
      {
        selectedAccount: getAccountByIndex(nextIndex),
        selectedSleeve: undefined,
        selectedOrder: undefined,
      },
      'ProposalAccountDetails.handleNextAccount',
    )
  }, [
    proposal,
    pageNumber,
    totalPages,
    getAccountByIndex,
    setQueryParams,
    onTogglePane,
  ])

  const handlePreviousAccount = useCallback(() => {
    if (!proposal || pageNumber === null || totalPages === null) return
    const prevIndex = (pageNumber - 1 + totalPages) % totalPages

    // Close the side pane when switching sleeves
    onTogglePane(null)

    setQueryParams(
      {
        selectedAccount: getAccountByIndex(prevIndex),
        selectedSleeve: undefined,
        selectedOrder: undefined,
      },
      'ProposalAccountDetails.handlePreviousAccount',
    )
  }, [
    proposal,
    pageNumber,
    totalPages,
    getAccountByIndex,
    setQueryParams,
    onTogglePane,
  ])

  //
  // Summary and compliance section memoized values
  //

  const allocationComplianceBeforeCheck = useMemo(() => {
    if (!before) return null

    const compliance = selectedSleeve
      ? (before.sleeves[selectedSleeve]?.compliance?.checks ?? [])
      : (before.compliance?.checks ?? [])

    // Look for either weight or market value compliance
    const weightCheck = compliance.find(
      (check: ComplianceCheck) =>
        check.type === ComplianceCheckType.WeightCompliance,
    ) as WeightCompliance | undefined

    const marketValueCheck = compliance.find(
      (check: ComplianceCheck) =>
        check.type === ComplianceCheckType.MarketValueCompliance,
    ) as MarketValueCompliance | undefined

    return weightCheck || marketValueCheck || null
  }, [before, selectedSleeve])

  const allocationComplianceAfterCheck = useMemo(() => {
    if (!after) return null
    const compliance = selectedSleeve
      ? (after.sleeves[selectedSleeve]?.compliance?.checks ?? [])
      : (after.compliance?.checks ?? [])

    // Look for either weight or market value compliance
    const weightCheck = compliance.find(
      (check: ComplianceCheck) =>
        check.type === ComplianceCheckType.WeightCompliance,
    ) as WeightCompliance | undefined

    const marketValueCheck = compliance.find(
      (check: ComplianceCheck) =>
        check.type === ComplianceCheckType.MarketValueCompliance,
    ) as MarketValueCompliance | undefined

    return weightCheck || marketValueCheck || null
  }, [after, selectedSleeve])

  const modelComplianceBeforeCheck = useMemo(() => {
    if (!before) return null
    const compliance = selectedSleeve
      ? (before.sleeves[selectedSleeve]?.compliance?.checks ?? [])
      : (before.compliance?.checks ?? [])
    return compliance.find(
      (check: ComplianceCheck) =>
        check.type === ComplianceCheckType.ModelCompliance,
    ) as ProposalModelCompliance
  }, [before, selectedSleeve])

  const modelComplianceAfterCheck = useMemo(() => {
    if (!after) return null
    const compliance = selectedSleeve
      ? (after.sleeves[selectedSleeve]?.compliance?.checks ?? [])
      : (after.compliance?.checks ?? [])

    return compliance.find(
      (check: ComplianceCheck) =>
        check.type === ComplianceCheckType.ModelCompliance,
    ) as ProposalModelCompliance
  }, [after, selectedSleeve])

  const targetNumberLineData = useMemo(() => {
    if (!allocationComplianceBeforeCheck || !allocationComplianceAfterCheck)
      return null

    // Get total market value for normalization if needed
    const totalMarketValue = selectedSleeve
      ? // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: suppress noUncheckedIndexedAccess
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        before?.sleeves[selectedSleeve]!.statistics.total_market_value
      : before?.statistics.total_market_value

    // Helper to normalize compliance check to weights (0-1 range)
    const normalizeToWeight = (
      check: WeightCompliance | MarketValueCompliance,
    ) => {
      if (check.type === ComplianceCheckType.WeightCompliance) {
        return {
          current: check.current_weight,
          target: check.target_weight,
          min: check.min_weight,
          max: check.max_weight,
        }
      } else {
        // MarketValueCompliance - convert to weights
        const tmv = totalMarketValue || 1 // Avoid division by zero
        return {
          current: check.current_market_value / tmv,
          target: check.target_market_value / tmv,
          min: check.min_market_value / tmv,
          max: check.max_market_value / tmv,
        }
      }
    }

    const beforeWeights = normalizeToWeight(allocationComplianceBeforeCheck)
    const afterWeights = normalizeToWeight(allocationComplianceAfterCheck)

    const currentWeight = beforeWeights.current * 100
    const proposedWeight = afterWeights.current * 100
    const targetWeight = afterWeights.target * 100
    const minWeight = beforeWeights.min * 100
    const maxWeight = beforeWeights.max * 100

    return {
      current: {
        value: currentWeight,
        label: formatPercentage(currentWeight, { numDigits: 1 }),
      },
      proposed: {
        value: proposedWeight,
        label: formatPercentage(proposedWeight, { numDigits: 1 }),
      },
      target: {
        value: targetWeight,
        label: formatPercentage(targetWeight, { numDigits: 1 }),
      },
      min: {
        value: minWeight,
        label: formatPercentage(minWeight, { numDigits: 1 }),
      },
      max: {
        value: maxWeight,
        label: formatPercentage(maxWeight, { numDigits: 1 }),
      },
    }
  }, [
    allocationComplianceBeforeCheck,
    allocationComplianceAfterCheck,
    before,
    selectedSleeve,
  ])

  const beforePositions = useMemo(() => {
    if (!before) return []
    const applicableLots = selectedSleeve
      ? before.lots.filter(
          (lot) =>
            selectedSleeve === undefined || lot.sleeve_id === selectedSleeve,
        )
      : before.lots
    return aggregatePosition(
      applicableLots,
      selectedSleeve
        ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          before.sleeves[selectedSleeve]!.statistics.total_market_value
        : before.statistics.tracked_fixed_income_market_value,
      !!selectedSleeve,
    )
  }, [before, selectedSleeve])

  const afterPositions = useMemo(() => {
    if (!after) return []
    const applicableLots = selectedSleeve
      ? after.lots.filter(
          (lot) =>
            selectedSleeve === undefined || lot.sleeve_id === selectedSleeve,
        )
      : after.lots
    return aggregatePosition(
      applicableLots,
      selectedSleeve
        ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          after.sleeves[selectedSleeve]!.statistics.total_market_value
        : after.statistics.tracked_fixed_income_market_value,
      !!selectedSleeve,
    )
  }, [after, selectedSleeve])

  const BeforeSummarySection = useMemo(() => {
    if (!before) {
      return null
    }

    if (activeTab === ProposalAccountDetailsTabs.Compliance) {
      return (
        <ComplianceList
          constraints={Object.values(
            modelComplianceBeforeCheck?.constraints || {},
          )}
          holdings={beforePositions}
          constraintContainerClassName="border border-primary-border rounded"
          className="gap-y-1"
          multiplyBy100={true}
        />
      )
    }

    return (
      <SummarySection
        accountDetails={before}
        selectedSleeve={selectedSleeve}
        chartContainerClassName="mt-0"
        distributionGridClassName="gap-1 mb-0"
        showOrderStats={false}
      />
    )
  }, [
    before,
    selectedSleeve,
    activeTab,
    modelComplianceBeforeCheck,
    beforePositions,
  ])

  const AfterSummarySection = useMemo(() => {
    if (!after) {
      return null
    }
    // Determine whether the current portfolio should be hidden (no fixed income)
    const hideBefore = (() => {
      if (before?.statistics.tracked_fixed_income_market_value === 0) {
        return true
      }
      if (selectedSleeve) {
        const beforeSleeve = before?.sleeves[selectedSleeve]
        if (!beforeSleeve) return true
        const sleeveFiTotal =
          beforeSleeve.statistics.agency_market_value +
          beforeSleeve.statistics.cd_market_value +
          beforeSleeve.statistics.corporate_market_value +
          beforeSleeve.statistics.government_market_value +
          beforeSleeve.statistics.municipal_market_value +
          beforeSleeve.statistics.treasury_market_value
        return sleeveFiTotal === 0
      }
      return false
    })()
    if (activeTab === ProposalAccountDetailsTabs.Compliance) {
      return (
        <ComplianceList
          constraints={Object.values(
            modelComplianceAfterCheck?.constraints || {},
          )}
          holdings={afterPositions}
          constraintContainerClassName="border border-primary-border rounded"
          className="gap-y-1"
          multiplyBy100={true}
          constraintInstanceType={ConstraintInstanceType.Proposal}
        />
      )
    }
    return (
      <SummarySection
        accountDetails={after}
        selectedSleeve={selectedSleeve}
        comparisonAccountDetails={!hideBefore ? before : undefined}
        showDeltas={!hideBefore}
        chartContainerClassName="mt-0"
        distributionGridClassName="gap-1 mb-0"
        proposal={proposal}
        selectedAccount={selectedAccount ?? undefined}
      />
    )
  }, [
    after,
    before,
    selectedSleeve,
    activeTab,
    modelComplianceAfterCheck,
    afterPositions,
    proposal,
    selectedAccount,
  ])

  //
  // Miscellaneous helpers
  //

  const sleeveOptions = useMemo(() => {
    const fullAccount = {
      label: 'Full account',
      value: 'full_account',
      subtext: 'Overview',
    }
    if (!after) return [fullAccount]

    const sleeveOptions = Object.values(after.sleeves ?? {}).map((sleeve) => {
      const modelCheck = sleeve.compliance?.checks?.find(
        (check) => check.type === ComplianceCheckType.ModelCompliance,
      ) as ProposalModelCompliance | undefined
      return {
        label: sleeve.name,
        value: sleeve.id,
        subtext:
          modelCheck?.model_name ||
          proposal?.models?.[modelCheck?.model_id || '']?.name ||
          'Unknown model',
      }
    })
    return [fullAccount, ...sleeveOptions]
  }, [after, proposal])

  const handleSearchChange = useCallback(
    (term: string) => {
      const searchTerm = term === '' ? undefined : term
      setQueryParams(
        { searchTerm },
        'ProposalAccountDetails.handleSearchChange',
      )
      return Promise.resolve()
    },
    [setQueryParams],
  )

  const shouldHideBeforeView = useMemo(() => {
    // For Compliance tab, hide if there are no securities (only cash)
    if (activeTab === ProposalAccountDetailsTabs.Compliance) {
      if (!before?.lots || before.lots.length === 0) {
        return true
      }
      // If a sleeve is selected, check if the sleeve has any lots
      if (selectedSleeve) {
        const sleeveLots = before.lots.filter(
          (lot) => lot.sleeve_id === selectedSleeve,
        )
        return sleeveLots.length === 0
      }
      return false
    }

    // For Summary tab, hide when there's no fixed income
    if (activeTab === ProposalAccountDetailsTabs.Summary) {
      // Hide before view if the account has no fixed income
      if (before?.statistics.tracked_fixed_income_market_value === 0) {
        return true
      }
      // Hide before view if the sleeve has no fixed income
      if (selectedSleeve) {
        const beforeSleeve = before?.sleeves[selectedSleeve]
        if (!beforeSleeve) return true
        return (
          beforeSleeve.statistics.agency_market_value +
            beforeSleeve.statistics.cd_market_value +
            beforeSleeve.statistics.corporate_market_value +
            beforeSleeve.statistics.government_market_value +
            beforeSleeve.statistics.municipal_market_value +
            beforeSleeve.statistics.treasury_market_value ===
          0
        )
      }
    }

    return false
  }, [before, selectedSleeve, activeTab])

  const [showPrompt, setShowPrompt] = useState(false)

  const handleClick = useCallback(() => {
    setShowPrompt((prev) => !prev)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleClick()
      }
    },
    [handleClick],
  )

  return (
    <ProposalAccountDetailsContainer>
      {selectedOrderToRemove && (
        <ConfirmOrderRemoveDialog
          open={isOrderRemoveDialogOpen}
          onOpenChange={setIsOrderRemoveDialogOpen}
          tradeDetails={selectedOrderToRemove}
          onConfirm={async () =>
            await handleOrderRemoveFromHoldings(selectedOrderToRemove.id)
          }
        />
      )}
      {selectedOrderForSwap && (
        <ProposalBondSwapDialog
          open={isSwapDialogOpen}
          onOpenChange={(open) => {
            setIsSwapDialogOpen(open)
            if (!open) {
              setSelectedOrderForSwap(null)
            }
          }}
          onConfirmSwap={handleSwapConfirm}
          proposalId={proposalId}
          order={selectedOrderForSwap}
        />
      )}
      {selectedOrderForEdit && (
        <ProposalEditOrderDialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open)
            if (!open) {
              setSelectedOrderForEdit(null)
            }
          }}
          proposalId={proposalId}
          order={selectedOrderForEdit}
        />
      )}
      <ProposalAccountDetailsMainContent>
        <ProposalAccountDetailsHeaderContainer>
          {/** biome-ignore lint/a11y/useButtonType: Bulk suppression as part of biome migration */}
          <button onClick={onBackToTable} className="cursor-pointer">
            <BodySubtext className="text-secondary">Back to table</BodySubtext>
          </button>
          <NavigationContainer>
            <div>
              <BodyXS>{pageNumber !== null ? pageNumber + 1 : '?'}</BodyXS>
            </div>
            <div>
              <BodyXS className="text-secondary">{`/ ${totalPages}`}</BodyXS>
            </div>
            <IconContainer onClick={handlePreviousAccount}>
              <Icon iconName={IconName.ChevronLeftIcon} size="small" outline />
            </IconContainer>
            <IconContainer onClick={handleNextAccount}>
              <Icon iconName={IconName.ChevronRightIcon} size="small" outline />
            </IconContainer>
            <VerticalSeparator />
          </NavigationContainer>
        </ProposalAccountDetailsHeaderContainer>
        <ProposalAccountDetailsContentLayout>
          <ProposalAccountDetailsContentContainer
            className={`${isSidePaneVisible ? 'pr-[30%]' : 'pr-0'}`}
          >
            <AccountMetadataAndSleeveSelectorContainer>
              <AccountMetadataContainer>
                <AccountNameAndManagementTypeContainer>
                  {after?.management_type && (
                    <Badge variant={BadgeVariant.Neutral}>
                      {after?.management_type?.toUpperCase()}
                    </Badge>
                  )}
                  <BodyDefault fontWeight={FontWeight.SemiBold}>
                    {after?.account_name}
                  </BodyDefault>
                </AccountNameAndManagementTypeContainer>
                <BodySubtext className="text-secondary pl-0.5">
                  {after?.custodian_account_id && after?.custodian_account_id}
                  {after?.custodian_account_id &&
                    after?.household.name &&
                    '・ '}
                  {after?.household.name && after?.household.name}
                </BodySubtext>
              </AccountMetadataContainer>
              <div className="flex items-center gap-2">
                {modelComplianceAfterCheck && selectedSleeve !== undefined && (
                  <div className="flex flex-row h-[28px] bg-tertiary-background pl-2 gap-[6px] items-center border border-primary-border rounded-md">
                    <BodyXS className="text-secondary">Model</BodyXS>
                    <div className="flex flex-row bg-background rounded rounded-r-0 border border-r-0 border-primary-border h-[28px] items-center px-2 py-[6px] gap-1">
                      <Image
                        src={'/./moment-logo.svg'}
                        className="logo"
                        alt="Moment logo"
                        height={14}
                        width={14}
                        priority
                      />
                      <BodyXS className="text-secondary">
                        {modelComplianceAfterCheck.model_name ||
                          proposal?.models?.[modelComplianceAfterCheck.model_id]
                            ?.name ||
                          'Unknown model'}
                      </BodyXS>
                    </div>
                  </div>
                )}
                {managementType !== ManagementType.Sma && (
                  <Select
                    options={sleeveOptions}
                    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                    defaultValue={sleeveOptions[0]!.value}
                    value={selectedSleeve}
                    onValueChange={(value) => {
                      // Close the side pane when switching sleeves
                      onTogglePane(null)

                      setQueryParams(
                        {
                          selectedSleeve:
                            value === 'full_account' ? undefined : value,
                          selectedOrder: undefined,
                        },
                        'ProposalAccountDetails.handleSleeveChange',
                      )
                    }}
                    className="w-[280px]"
                  />
                )}
              </div>
            </AccountMetadataAndSleeveSelectorContainer>
            <ContentContainer>
              {!!after?.extra?.prompt &&
                activeTab === ProposalAccountDetailsTabs.Summary && (
                  <PromptSectionContainer
                    prompt={after?.extra?.prompt as string | null}
                    showPrompt={showPrompt}
                    handleClick={handleClick}
                    handleKeyDown={handleKeyDown}
                  />
                )}
              {managementType !== ManagementType.Sma &&
                selectedSleeve !== undefined &&
                activeTab === ProposalAccountDetailsTabs.Summary &&
                (targetNumberLineData?.min?.value !== 0 ||
                  targetNumberLineData?.max?.value !== 0 ||
                  targetNumberLineData?.target?.value !== 0) && ( // Don't show target number line if min&max&target are not set
                  <PortfolioAllocationContainer>
                    <PortfolioHeaderContainer>
                      <SwatchAndTitleContainer>
                        <ColorSwatch color="#cbcbcb" />
                        <BodySmall fontWeight={FontWeight.Medium}>
                          Portfolio allocation
                        </BodySmall>
                      </SwatchAndTitleContainer>
                      <BodySubtext className="text-secondary">
                        % of total account value
                      </BodySubtext>
                    </PortfolioHeaderContainer>

                    <TargetNumberLine
                      // Hide the current value if the before view is hidden, which means the account has no fixed income
                      current={
                        shouldHideBeforeView
                          ? undefined
                          : targetNumberLineData?.current
                      }
                      proposed={targetNumberLineData?.proposed}
                      target={targetNumberLineData?.target}
                      min={targetNumberLineData?.min}
                      max={targetNumberLineData?.max}
                      className="w-full bg-background max-w-screen p-3 border border-primary-border"
                    />
                  </PortfolioAllocationContainer>
                )}
              {/* Don't show compliance statistics for UMAs if no sleeve is selected */}
              {managementType !== ManagementType.Sma &&
                selectedSleeve === undefined &&
                activeTab === ProposalAccountDetailsTabs.Compliance && (
                  <SelectPortfolioOverlayContainer>
                    <BodySmall className="text-secondary">
                      Select a portfolio to view compliance statistics
                    </BodySmall>
                  </SelectPortfolioOverlayContainer>
                )}
              {!(
                managementType !== ManagementType.Sma &&
                selectedSleeve === undefined &&
                activeTab === ProposalAccountDetailsTabs.Compliance
              ) &&
                activeTab !== ProposalAccountDetailsTabs.Actions && (
                  // biome-ignore lint/complexity/noUselessFragments: Bulk suppression as part of biome migration
                  <>
                    {activeTab === ProposalAccountDetailsTabs.Compliance &&
                    !modelComplianceBeforeCheck ? (
                      <SelectPortfolioOverlayContainer>
                        <BodySmall className="text-secondary">
                          Sleeve not assigned to a model
                        </BodySmall>
                      </SelectPortfolioOverlayContainer>
                    ) : (
                      <BeforeAndAfterContainer>
                        {!shouldHideBeforeView && (
                          <PortfolioOverviewContainer>
                            <PortfolioHeaderContainer>
                              <SwatchAndTitleContainer>
                                <ColorSwatch color="#CC6300" />
                                <BodySmall fontWeight={FontWeight.Medium}>
                                  Current portfolio
                                </BodySmall>
                              </SwatchAndTitleContainer>
                              {(managementType === ManagementType.Sma ||
                                selectedSleeve === undefined) && (
                                <BodySubtext className="text-secondary">
                                  {`Cash: ${formatMoney(before?.statistics.cash_market_value ?? 0)}`}
                                </BodySubtext>
                              )}
                            </PortfolioHeaderContainer>
                            <SectionContainer>
                              {BeforeSummarySection}
                            </SectionContainer>
                          </PortfolioOverviewContainer>
                        )}
                        <PortfolioOverviewContainer
                          spanFullWidth={shouldHideBeforeView}
                        >
                          <PortfolioHeaderContainer>
                            <SwatchAndTitleContainer>
                              <ColorSwatch color="#005C9E" />
                              <BodySmall fontWeight={FontWeight.Medium}>
                                Proposed portfolio
                              </BodySmall>
                            </SwatchAndTitleContainer>
                            {(managementType === ManagementType.Sma ||
                              selectedSleeve === undefined) && (
                              <div className="flex items-center gap-1">
                                {isNegativeCash && (
                                  <Tooltip simpleText="Cash is negative. Adjust quantities or limit prices to bring the balance back to positive.">
                                    <Icon
                                      iconName={
                                        IconName.ExclamationTriangleIcon
                                      }
                                      size="small"
                                      className="text-moment-red-700 dark:text-moment-red-400"
                                    />
                                  </Tooltip>
                                )}
                                <BodySubtext
                                  className={
                                    isNegativeCash
                                      ? 'text-moment-red-700 dark:text-moment-red-400'
                                      : 'text-secondary'
                                  }
                                >
                                  {`Cash: ${formatMoney(proposedCash)}`}
                                </BodySubtext>
                              </div>
                            )}
                          </PortfolioHeaderContainer>
                          <SectionContainer>
                            {AfterSummarySection}
                          </SectionContainer>
                        </PortfolioOverviewContainer>
                      </BeforeAndAfterContainer>
                    )}
                  </>
                )}
              {activeTab === ProposalAccountDetailsTabs.Actions && (
                <AccountHoldingsContainer>
                  <HoldingsBorderContainer>
                    <SearchContainer>
                      <Search
                        placeholder="Search holdings..."
                        value={searchTerm}
                        onInputChange={handleSearchChange}
                      />
                    </SearchContainer>
                  </HoldingsBorderContainer>
                  <ProposalAccountHoldingsTable
                    accountDetails={after}
                    previousAccountDetails={before}
                    proposalOrders={proposal?.orders ?? []}
                    isLoading={false}
                    onTogglePane={onTogglePane}
                    onOrderRemoveAction={handleOrderRemoveAction}
                    onOrderSwapAction={handleOrderSwapAction}
                    onOrderEditAction={handleOrderEditAction}
                    rowNodeLoading={rowNodeLoading}
                  />
                </AccountHoldingsContainer>
              )}
            </ContentContainer>
          </ProposalAccountDetailsContentContainer>
        </ProposalAccountDetailsContentLayout>
      </ProposalAccountDetailsMainContent>
    </ProposalAccountDetailsContainer>
  )
}
